import { useState, forwardRef, useEffect, useRef, useId } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LocaleSwitcher from "@/components/LocaleSwitcher";
import { MyelinaMark } from "@/components/brand/MyelinaMark";
import { Button } from "@/components/ui/button";
import { CtaButton } from "@/components/cta/CtaButton";
import { cn } from "@/lib/utils";
import { prefetchOn } from "@/lib/prefetchRoute";
import { scrollToSection } from "@/lib/scrollToSection";

import { trackCtaClick } from "@/lib/observability";
import { openRequestAccess } from "@/components/landing/RequestAccessDialog";

const Navbar = forwardRef<HTMLElement>((_, ref) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const mobileMenuId = useId();
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Escape to close + focus management + basic focus trap
  useEffect(() => {
    if (!mobileOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Focus first focusable item in the menu
    const focusables = () =>
      mobileMenuRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? ([] as unknown as NodeListOf<HTMLElement>);

    const first = focusables()[0];
    first?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setMobileOpen(false);
        toggleButtonRef.current?.focus();
        return;
      }
      if (e.key === "Tab") {
        const nodes = Array.from(focusables());
        if (nodes.length === 0) return;
        const firstEl = nodes[0];
        const lastEl = nodes[nodes.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && active === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [mobileOpen]);

  const scrollTo = (id: string) => {
    void scrollToSection(id);
    setMobileOpen(false);
  };


  return (
    <nav
      ref={ref}
      aria-label="Main navigation"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        scrolled
          ? "bg-background/70 backdrop-blur-2xl shadow-[0_1px_0_0_hsl(var(--border)/0.5)]"
          : "bg-transparent",
      )}
    >
      {scrolled && (
        <div
          aria-hidden="true"
          className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-border to-transparent"
        />
      )}
      <div className="container mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group pr-4 mr-2 border-r border-border/40 shrink-0 whitespace-nowrap" aria-label="Myelina Health, home">
            <MyelinaMark className="w-5 h-5 text-primary transition-transform group-hover:scale-110" aria-hidden="true" />
            <span className="font-display text-base text-foreground tracking-tight">
              Myelina Health<span className="text-[9px] align-top ml-0.5 text-muted-foreground">™</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-0.5">
            {[
              { id: "how-it-works", label: "How it works" },
              { id: "instrument", label: "The instrument" },
              { id: "features", label: "Features" },
              { id: "faq", label: "FAQ" },
            ].map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => scrollTo(l.id)}
                aria-label={`Scroll to ${l.label} section`}
                className="px-2.5 py-2 text-sm font-body whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {l.label}
              </button>
            ))}
            <Link to="/product" {...prefetchOn("/product")} className="px-2.5 py-2 text-sm font-body whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background">Product</Link>
            <Link to="/who-we-are" {...prefetchOn("/who-we-are")} className="px-2.5 py-2 text-sm font-body whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background">Who we are</Link>
            <Link to="/blog" {...prefetchOn("/blog")} className="px-2.5 py-2 text-sm font-body whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background">Blog</Link>
            <Link to="/for-clinicians" {...prefetchOn("/for-clinicians")} className="hidden xl:inline-flex px-2.5 py-2 text-sm font-body whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background">Clinicians</Link>
            <Link to="/demo" {...prefetchOn("/demo")} className="hidden xl:inline-flex px-2.5 py-2 text-sm font-body whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background">Demo</Link>

            <div className="ml-2"><LocaleSwitcher /></div>

            <Link to="/login" {...prefetchOn("/login")} className="ml-2 px-2.5 py-2 text-sm font-body whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background">
              Log in
            </Link>
            <CtaButton
              intent="requestAccess"
              size="sm"
              source="navbar_desktop"
              analyticsMetadata={{ signup: true, destination: "testflight" }}
              onClick={() => window.open("https://testflight.apple.com/join/fA3q9hwF", "_blank", "noopener,noreferrer")}
              aria-label="Request access, opens TestFlight in a new tab"
              className="ml-2"
              showArrow={false}
            />


          </div>

          <div className="lg:hidden flex items-center gap-2">
            <LocaleSwitcher />
            <button
              ref={toggleButtonRef}
              type="button"
              className="p-2 -mr-2 text-foreground rounded-md hover:bg-muted transition-colors min-h-[44px] min-w-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileOpen}
              {...(mobileOpen ? { "aria-controls": mobileMenuId } : {})}
              aria-haspopup="menu"
            >
              {mobileOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              ref={mobileMenuRef}
              id={mobileMenuId}
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl overflow-hidden"
            >
              <ul className="py-3 space-y-1.5 list-none" role="menu">
                {[
                  { id: "how-it-works", label: "How it works" },
                  { id: "instrument", label: "The instrument" },
                  { id: "features", label: "Features" },
                  { id: "faq", label: "FAQ" },
                ].map((l) => (
                  <li key={l.id} role="none">
                    <button role="menuitem" type="button" onClick={() => scrollTo(l.id)} aria-label={`Scroll to ${l.label} section`} className="flex items-center justify-between w-full px-4 min-h-12 py-3 text-left text-foreground hover:bg-muted rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                      <span className="font-body text-[15px]">{l.label}</span>
                      <ChevronRight className="w-4 h-4 opacity-50" aria-hidden="true" />
                    </button>
                  </li>
                ))}
                {[
                  { to: "/product", label: "Product" },
                  { to: "/who-we-are", label: "Who we are" },
                  { to: "/blog", label: "Blog" },
                  { to: "/for-clinicians", label: "Clinicians" },
                  { to: "/demo", label: "Demo" },
                ].map((l) => {
                  const isCurrent = location.pathname === l.to;
                  return (
                    <li key={l.to} role="none">
                      <Link
                        to={l.to}
                        role="menuitem"
                        aria-current={isCurrent ? "page" : undefined}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-between px-4 min-h-12 py-3 text-foreground hover:bg-muted rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-[current=page]:bg-muted aria-[current=page]:font-medium"
                      >
                        <span className="font-body text-[15px]">{l.label}</span>
                        <ChevronRight className="w-4 h-4 opacity-50" aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
                <li role="none" className="px-4 pt-4 mt-2 space-y-3 border-t border-border">
                  <Link to="/login" role="menuitem" onClick={() => setMobileOpen(false)} className="flex items-center min-h-11 py-2 text-[15px] text-muted-foreground hover:text-foreground rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background">Web app log in</Link>
                  <CtaButton
                    intent="requestAccess"
                    source="navbar_mobile"
                    analyticsMetadata={{ signup: true, destination: "testflight" }}
                    onClick={() => {
                      setMobileOpen(false);
                      window.open("https://testflight.apple.com/join/fA3q9hwF", "_blank", "noopener,noreferrer");
                    }}
                    className="w-full"
                    showArrow={false}
                    aria-label="Request access, opens TestFlight in a new tab"
                  />
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
});

Navbar.displayName = "Navbar";

export default Navbar;
